import React, { useEffect, useRef, useState } from "react";
import { X, ArrowUp, ArrowDown, Copy, Plus, Keyboard } from "lucide-react";
import { TYPE_LABEL, CATS, catKey, isChoice } from "./intakeShared";

function QText({ value, onCommit, testid }) {
  const [v, setV] = useState(value);
  const ref = useRef(null);
  useEffect(() => setV(value), [value]);
  useEffect(() => { const el = ref.current; if (el) { el.style.height = "auto"; el.style.height = `${el.scrollHeight}px`; } }, [v]);
  const commit = () => { const t = v.trim(); if (t !== value) onCommit(t); else setV(value); };
  return <textarea ref={ref} rows={1} value={v} onChange={(e) => setV(e.target.value)} onBlur={commit} aria-label="Question text" placeholder="Type the question"
    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } }}
    className="in-ta text-[14px] font-medium text-[var(--ink)] leading-snug" data-testid={testid} />;
}

export const withType = (q, type) => {
  if (type === "yes_no") return { ...q, type, options: ["Yes", "No"] };
  if (type === "text") return { ...q, type, options: [] };
  const keep = isChoice(q.type) && (q.options || []).length ? q.options : ["", ""];
  return { ...q, type, options: keep };
};

function Options({ q, n, onChange }) {
  const opts = q.options || [];
  const set = (i, v) => onChange({ ...q, options: opts.map((o, k) => (k === i ? v : o)) });
  return (
    <>
      {opts.map((o, oi) => (
        <span key={oi} className="in-chip inline-flex items-center gap-0.5 bg-[#F1F1F4] rounded-full" style={{ padding: "1px 4px 1px 9px" }}>
          <input value={o} onChange={(e) => set(oi, e.target.value)} placeholder="Option" size={Math.max(4, o.length)} className="in-opt" aria-label={`Option ${oi + 1}`} data-testid={`in-q-opt-${n}-${oi}`} />
          <button type="button" className="in-x w-4 h-4 rounded-full inline-flex items-center justify-center hover:bg-black/[.08]" aria-label={`Remove option ${o || oi + 1}`}
            onClick={() => onChange({ ...q, options: opts.filter((_, k) => k !== oi) })} data-testid={`in-q-opt-x-${n}-${oi}`}><X size={10} /></button>
        </span>
      ))}
      <button type="button" onClick={() => onChange({ ...q, options: [...opts, ""] })} className="inline-flex items-center gap-0.5 text-[12px] font-semibold rounded-full border border-dashed border-[#C9C9D6] text-[#4A4AC2] hover:bg-[#F3F3FD]" style={{ padding: "1px 9px" }} data-testid={`in-q-opt-add-${n}`}><Plus size={11} />Add option</button>
    </>
  );
}

const IconBtn = ({ label, onClick, disabled, testid, children, danger }) => (
  <button type="button" aria-label={label} title={label} onClick={onClick} disabled={disabled} data-testid={testid}
    className={`w-[26px] h-[26px] rounded-full inline-flex items-center justify-center text-[var(--gray3)] disabled:opacity-30 ${danger ? "in-x in-qx" : "hover:bg-black/[.05] hover:text-[var(--ink)]"}`}>{children}</button>
);

export function QRow({ q, n, onChange, onRemove, onDup, onUp, onDown }) {
  const choice = isChoice(q.type), filled = (q.options || []).filter((o) => String(o).trim()).length;
  return (
    <div className="in-row flex gap-[10px] border-t border-[#E9E9EC] first:border-t-0" style={{ padding: "11px 8px 11px 14px" }} data-testid={`in-q-${n}`}>
      <span className="text-[12px] text-[var(--gray3)] w-4 flex-none pt-[2px] tabular-nums">{n}</span>
      <div className="flex-1 min-w-0">
        <QText value={q.question} onCommit={(t) => onChange({ ...q, question: t })} testid={`in-q-text-${n}`} />
        <div className="flex flex-wrap items-center gap-1.5 mt-[5px]">
          <select value={q.type in TYPE_LABEL ? q.type : "text"} onChange={(e) => onChange(withType(q, e.target.value))} className="in-sel" aria-label="Answer type" data-testid={`in-q-type-${n}`}>
            {Object.entries(TYPE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <select value={catKey(q.category)} onChange={(e) => onChange({ ...q, category: e.target.value })} className="in-sel" aria-label="Section" data-testid={`in-q-cat-${n}`}>
            {CATS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          {q.type === "yes_no" ? <span className="text-[12px] text-[var(--gray2)] bg-[#F1F1F4] rounded-full px-2.5 py-[1px]">Yes · No</span> : null}
          {choice ? <Options q={q} n={n} onChange={onChange} /> : null}
          {q.type === "text" || !(q.type in TYPE_LABEL) ? <span className="inline-flex items-center gap-1 text-[11px] text-[#9A5A00]" title="The intake works best when patients can tap an answer" data-testid={`in-q-typing-${n}`}><Keyboard size={11} />patients must type</span> : null}
          <button type="button" aria-pressed={!!q.new_patient_only} onClick={() => onChange({ ...q, new_patient_only: !q.new_patient_only })} data-testid={`in-q-new-${n}`}
            className={`text-[11px] font-semibold px-1.5 py-[1px] rounded-[5px] border transition-colors ${q.new_patient_only ? "border-[#5856D6] text-[#4A4AC2] bg-[#F3F3FD]" : "in-ctl border-dashed border-[#D4D4DA] text-[var(--gray3)]"}`}>
            {q.new_patient_only ? "New patients only" : "+ New patients only"}</button>
        </div>
        {choice && filled < 2 ? <div className="text-[12px] mt-1 text-[#9A5A00]" data-testid={`in-q-optwarn-${n}`}>Needs at least 2 options, or it's saved as a typed answer.</div> : null}
        {!q.question.trim() ? <div className="text-[12px] mt-1 text-[#9A5A00]" data-testid={`in-q-emptywarn-${n}`}>This question has no wording yet.</div> : null}
      </div>
      <div className="flex items-start gap-0.5 flex-none">
        <span className="in-ctl inline-flex">
          <IconBtn label="Move up" onClick={onUp} disabled={!onUp} testid={`in-q-up-${n}`}><ArrowUp size={13} /></IconBtn>
          <IconBtn label="Move down" onClick={onDown} disabled={!onDown} testid={`in-q-down-${n}`}><ArrowDown size={13} /></IconBtn>
          <IconBtn label="Duplicate" onClick={onDup} testid={`in-q-dup-${n}`}><Copy size={13} /></IconBtn>
        </span>
        <IconBtn label={`Remove question ${n}`} onClick={onRemove} testid={`in-q-x-${n}`} danger><X size={14} /></IconBtn>
      </div>
    </div>
  );
}
